import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Lightweight SRS due-count context: polls `/microlearning/flashcards/due-count`
 * every 3 minutes when the user is signed in. Consumers subscribe via `useSRSCount()`
 * to render nav badges, home nudges, etc.
 */
const SRSCountCtx = createContext({ dueToday: 0, overdue: 0, refresh: () => {} });

export function SRSCountProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ dueToday: 0, overdue: 0 });

  const refresh = useCallback(async () => {
    if (!user) { setState({ dueToday: 0, overdue: 0 }); return; }
    try {
      const { data } = await api.get("/microlearning/flashcards/due-count");
      setState({ dueToday: data.due_today || 0, overdue: data.overdue || 0 });
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user) return;
    const iv = setInterval(refresh, 3 * 60 * 1000);  // 3-minute polling
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [user, refresh]);

  return (
    <SRSCountCtx.Provider value={{ ...state, refresh }}>
      {children}
    </SRSCountCtx.Provider>
  );
}

export const useSRSCount = () => useContext(SRSCountCtx);
